import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { AlertCircle, CalendarDate, Card, EmptyState, Pagination, Table, Tabs } from "@opendate-app/setlist";
import { eventAudienceColumns } from "./EventAudienceRow";

const TABS = [
  { value: "Upcoming Events", label: "Upcoming" },
  { value: "Past Events", label: "Past" },
];

const groupStyle = (index) => ({ padding: "4px 8px", borderTop: index > 0 ? "1px solid var(--sl-border-secondary)" : "none" });

const groupByMonth = (rows) => rows.reduce((groups, event) => {
  const label = event.month_label || "No date";
  const last = groups[groups.length - 1];
  if (last && last.label === label) {
    last.events.push(event);
  } else {
    groups.push({ label, events: [event] });
  }
  return groups;
}, []);

const EventAudiencesList = ({ dataSource, teamId, searchTerm, onResultCount }) => {
  const searching = Boolean(searchTerm);
  const [tab, setTab] = useState("Upcoming Events");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, per_page: 10, total_entries: 0, total_pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Reset page to 1 whenever tab/searchTerm change, adjusted during render (not
  // in a separate effect) so the fetch effect below only ever runs once per
  // filter change, with the corrected page — never a stale-page request.
  const [prevFilters, setPrevFilters] = useState({ tab, searchTerm });
  if (prevFilters.tab !== tab || prevFilters.searchTerm !== searchTerm) {
    setPrevFilters({ tab, searchTerm });
    if (page !== 1) setPage(1);
  }

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(false);
    axios
      .get(dataSource, { params: { page, per_page: 10, tab: searching ? undefined : tab, q: searchTerm || undefined } })
      .then(({ data }) => {
        if (!active) return;
        setRows(data.data || []);
        setPagination(data.pagination || {});
        if (onResultCount) onResultCount(data.pagination ? data.pagination.total_entries : 0);
      })
      .catch(() => {
        if (!active) return;
        setError(true);
        setRows([]);
        setPagination({ current_page: 1, per_page: 10, total_entries: 0, total_pages: 1 });
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [dataSource, page, tab, searchTerm, searching]);

  const renderBody = () => {
    if (error) {
      return <EmptyState size="sm" icon={AlertCircle} title="Couldn’t load events" description="Please try again." />;
    }
    if (rows.length === 0 && loading) {
      return <Table columns={eventAudienceColumns(teamId, "Event")} data={[]} loading loadingLabel="Loading events" />;
    }
    if (rows.length === 0) {
      return (
        <EmptyState
          size="sm"
          icon={CalendarDate}
          title={searching ? `No events match “${searchTerm}”.` : "No events yet."}
        />
      );
    }
    return groupByMonth(rows).map(({ label, events }, index) => (
      <div key={`${label}-${events[0].id}`} style={groupStyle(index)}>
        <Table columns={eventAudienceColumns(teamId, label)} data={events} getRowKey={(event) => event.id} stackBelow={640} />
      </div>
    ));
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
      {!searching && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Tabs
            variant="button-border"
            size="sm"
            collapseBelow={0}
            aria-label="Event timeframe"
            items={TABS}
            value={tab}
            onChange={setTab}
          />
        </div>
      )}

      <Card padding="none">{renderBody()}</Card>

      <Pagination
        currentPage={pagination.current_page}
        totalPages={pagination.total_pages}
        onPageChange={(p) => { if (!loading) setPage(p); }}
      />
    </div>
  );
};

EventAudiencesList.propTypes = {
  dataSource: PropTypes.string.isRequired,
  teamId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  searchTerm: PropTypes.string,
  onResultCount: PropTypes.func,
};

export default EventAudiencesList;
